import { AssetTag } from '@/feature/tags/components'
import { Asset } from '@/lib/features/Assets/interfaces'

import { PendingTagChange, TagBulkState } from './BulkTagsModal.types'

/** Maximum number of tags the backend allows on a single asset. */
export const MAX_TAGS_PER_ASSET = 10

export function computeTagStates(selectedAssets: Asset[], tags: AssetTag[]): Map<number, TagBulkState> {
  const states = new Map<number, TagBulkState>()
  const count = selectedAssets.length
  if (count === 0) return states

  for (const tag of tags) {
    const withTag = selectedAssets.filter((a) => a.tags?.some((t) => t.id === tag.id)).length
    if (withTag === 0) states.set(tag.id, 'none')
    else if (withTag === count) states.set(tag.id, 'all')
    else states.set(tag.id, 'some')
  }

  return states
}

/**
 * Overlays pending add/remove changes onto the base tag states so the UI can
 * reflect the user's edits before they are applied. An 'add' forces 'all', a
 * 'remove' forces 'none'.
 */
export function mergePendingIntoStates(
  baseStates: Map<number, TagBulkState>,
  pendingChanges: Map<number, PendingTagChange>,
): Map<number, TagBulkState> {
  const states = new Map(baseStates)
  for (const [tagId, action] of pendingChanges) {
    states.set(tagId, action === 'add' ? 'all' : 'none')
  }
  return states
}

/**
 * Computes the next set of pending changes when a tag row (in 'none' or 'all'
 * state) is toggled. Toggling back to a tag's original state cancels the
 * pending change rather than queuing a redundant one.
 */
export function computeToggleChange(
  prev: Map<number, PendingTagChange>,
  tagId: number,
  current: TagBulkState,
  base: TagBulkState,
): Map<number, PendingTagChange> {
  const next = new Map(prev)

  if (current === 'none') {
    // Toggling on: queue an add only if it wasn't already applied; otherwise cancel a pending remove
    if (base === 'none') next.set(tagId, 'add')
    else next.delete(tagId)
  } else {
    // Toggling off: queue a remove only if it was originally applied; otherwise cancel a pending add
    if (base === 'all') next.set(tagId, 'remove')
    else next.delete(tagId)
  }

  return next
}

export function computeAssetTagUpdates(
  selectedAssets: Asset[],
  pendingChanges: Map<number, PendingTagChange>,
): { assetId: number; tagIds: number[] }[] {
  return selectedAssets.map((asset) => {
    const currentIds = new Set(asset.tags?.map((t) => t.id) ?? [])

    for (const [tagId, action] of pendingChanges) {
      if (action === 'add') currentIds.add(tagId)
      else currentIds.delete(tagId)
    }

    return { assetId: asset.id, tagIds: [...currentIds] }
  })
}

/** Resolves an asset's human-facing label (bridgeId), falling back to its id. */
export function getAssetLabel(selectedAssets: Asset[], assetId: number): string {
  return selectedAssets.find((a) => a.id === assetId)?.bridgeId ?? String(assetId)
}

/**
 * Validates that no selected asset would exceed the tag limit once pending
 * changes are applied. Returns a user-facing error naming the offending
 * assets, or null when the changes are safe to apply.
 */
export function computeTagLimitError(
  selectedAssets: Asset[],
  pendingChanges: Map<number, PendingTagChange>,
  max: number = MAX_TAGS_PER_ASSET,
): string | null {
  if (pendingChanges.size === 0) return null

  const overLimit = computeAssetTagUpdates(selectedAssets, pendingChanges).filter(
    (u) => u.tagIds.length > max,
  )
  if (overLimit.length === 0) return null

  const labels = overLimit.map((u) => getAssetLabel(selectedAssets, u.assetId)).join(', ')
  return `Cannot apply — the following asset${overLimit.length > 1 ? 's' : ''} would exceed the ${max}-tag limit: ${labels}.`
}
